import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { m } from "@recobra/i18n/messages";
import type { Task, TaskReminder } from "@recobra/schemas/task";

import { tasksApi } from "../../lib/api";
import { getErrorMessage } from "../../lib/api";

/**
 * Saves a reminder against the cached task before the request resolves. A new
 * reminder is written under the client id it was created with and swapped for
 * the server record on success, so the row is never rendered twice. The
 * caller keeps owning the draft until then: a failed save rolls the cache back
 * and leaves the row in place with what the user typed.
 */
export const useSaveTaskReminder = ({
  task,
  reminder,
  isNew,
  scheduledDate,
  comment,
  onStored,
}: {
  task: Task;
  reminder: TaskReminder;
  isNew: boolean;
  scheduledDate: string;
  comment: string;
  onStored: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    scope: reminderMutationScope(task.id),
    meta: { suppressErrorToast: true },
    mutationFn: () =>
      isNew
        ? tasksApi.createReminder(task.id, reminderInput(scheduledDate, comment))
        : tasksApi.updateReminder(task.id, reminder.id, reminderInput(scheduledDate, comment)),
    onMutate: async () => {
      const previousTask = await takeTaskSnapshot(queryClient, task.id);
      patchTask(queryClient, task, (current) =>
        upsertReminder(current, {
          id: reminder.id,
          status: reminder.status,
          createdBy: reminder.createdBy,
          createdAt: reminder.createdAt,
          ...(reminder.dedupeKey ? { dedupeKey: reminder.dedupeKey } : {}),
          ...reminderInput(scheduledDate, comment),
        }),
      );

      return { previousTask };
    },
    onSuccess: (savedReminder) => {
      patchTask(queryClient, task, (current) =>
        upsertReminder(removeReminder(current, reminder.id), savedReminder),
      );
      if (isNew) onStored();
    },
    onError: (error, _input, context) => {
      queryClient.setQueryData<Task>(["tasks", task.id], context?.previousTask);
      toast.error(
        isNew
          ? m.task_reminders_create_error({ message: getErrorMessage(error) })
          : m.task_reminders_update_error({ message: getErrorMessage(error) }),
      );
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ["tasks"] }),
  });
};

export const useRemoveTaskReminder = (task: Task, reminderId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    scope: reminderMutationScope(task.id),
    meta: { suppressErrorToast: true },
    mutationFn: () => tasksApi.removeReminder(task.id, reminderId),
    onMutate: async () => {
      const previousTask = await takeTaskSnapshot(queryClient, task.id);
      patchTask(queryClient, task, (current) => removeReminder(current, reminderId));

      return { previousTask };
    },
    onError: (error, _input, context) => {
      queryClient.setQueryData<Task>(["tasks", task.id], context?.previousTask);
      toast.error(m.task_reminders_remove_error({ message: getErrorMessage(error) }));
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ["tasks"] }),
  });
};

/**
 * Runs one task's reminder mutations at a time, so an overlapping save and
 * removal cannot snapshot each other's half-applied cache and roll it back.
 */
const reminderMutationScope = (taskId: string) => ({ id: `task-reminders-${taskId}` });

const takeTaskSnapshot = async (queryClient: QueryClient, taskId: string) => {
  await queryClient.cancelQueries({ queryKey: ["tasks", taskId] });

  return queryClient.getQueryData<Task>(["tasks", taskId]);
};

/**
 * The modal renders a list row as placeholder data while the single-task read
 * is in flight, and `useTaskModalData` keeps that row out of the cache. Falling
 * back to it here means a reminder edited in that window still applies
 * optimistically; the `onSettled` invalidation replaces it with the full record.
 */
const patchTask = (queryClient: QueryClient, task: Task, patch: (task: Task) => Task) =>
  queryClient.setQueryData<Task>(["tasks", task.id], (cached) => patch(cached ?? task));

const upsertReminder = (task: Task, reminder: TaskReminder): Task => ({
  ...task,
  reminders: task.reminders.some((candidate) => candidate.id === reminder.id)
    ? task.reminders.map((candidate) => (candidate.id === reminder.id ? reminder : candidate))
    : [...task.reminders, reminder],
});

const removeReminder = (task: Task, reminderId: string): Task => ({
  ...task,
  reminders: task.reminders.filter((candidate) => candidate.id !== reminderId),
});

const reminderInput = (scheduledDate: string, comment: string) => ({
  scheduledDate,
  ...(comment.trim() ? { comment: comment.trim() } : {}),
});
